// src/composables/useFilterStorage.js
import { ref } from 'vue'

const STORAGE_KEY = 'prematchFilter'
const EXPIRY_TIME = 60 * 60 * 1000 // 1 hour in milliseconds

/**
 * Composable for managing filter data in session storage
 * This allows filtering behind the scenes without exposing parameters in URL
 */
export function useFilterStorage() {
  const storedFilter = ref(null)

  /**
   * Store filter data in session storage
   * @param {Object} filterData - Filter configuration object
   * @param {string} filterData.tab - Active tab (highlight, boosted, upcoming)
   * @param {number} filterData.tournament_id - Tournament ID for filtering
   * @param {number} filterData.sport_id - Sport ID
   * @param {number} filterData.market_id - Market ID
   * @param {number} filterData.page_size - Page size for pagination
   * @param {number} filterData.page - Current page
   */
  const setFilter = (filterData) => {
    const dataWithTimestamp = {
      ...filterData,
      timestamp: Date.now(),
    }

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataWithTimestamp))
      storedFilter.value = dataWithTimestamp
      console.log('Filter data stored:', dataWithTimestamp)
    } catch (error) {
      console.warn('Failed to store filter data:', error)
    }
  }

  /**
   * Get filter data from session storage
   * @returns {Object|null} Filter data or null if not found/expired
   */
  const getFilter = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY)
      if (!stored) return null

      const filterData = JSON.parse(stored)

      // Check if data has expired
      if (Date.now() - filterData.timestamp > EXPIRY_TIME) {
        clearFilter()
        return null
      }

      storedFilter.value = filterData
      return filterData
    } catch (error) {
      console.warn('Error reading stored filter data:', error)
      clearFilter()
      return null
    }
  }

  /**
   * Clear stored filter data
   */
  const clearFilter = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY)
      storedFilter.value = null
    } catch (error) {
      console.warn('Error clearing filter data:', error)
    }
  }

  /**
   * Check if there's a valid stored filter
   * @returns {boolean}
   */
  const hasStoredFilter = () => {
    return getFilter() !== null
  }

  /**
   * Get specific tournament filter if exists
   * @returns {Object|null} Tournament filter data
   */
  const getTournamentFilter = () => {
    const filter = getFilter()
    return filter && filter.tab === 'upcoming' && filter.tournament_id ? filter : null
  }

  /**
   * Update filter expiry (extend the life of current filter)
   */
  const refreshFilter = () => {
    const current = getFilter()
    if (current) {
      setFilter({
        ...current,
        timestamp: Date.now(),
      })
    }
  }

  // Initialize stored filter state
  getFilter()

  return {
    storedFilter: storedFilter.value,
    setFilter,
    getFilter,
    clearFilter,
    hasStoredFilter,
    getTournamentFilter,
    refreshFilter,
  }
}
